// Basic directional flex containers
export const flexRow = { display: "flex", flexDirection: "row" } as const;
export const flexCol = { display: "flex", flexDirection: "column" } as const;

// Common row combinations (alignItems / justifyContent)
export const flexRowStart = { ...flexRow, alignItems: "flex-start" } as const;
export const flexRowCenter = { ...flexRow, alignItems: "center", justifyContent: "center" } as const;
export const flexRowACenter = { ...flexRow, alignItems: "center" } as const; // alias
export const flexRowBetween = { ...flexRow, alignItems: "center", justifyContent: "space-between" } as const;
export const flexRowJBetweenAStart = { ...flexRow, alignItems: "flex-start", justifyContent: "space-between" } as const;
export const flexRowEnd = { ...flexRow, alignItems: "center", justifyContent: "flex-end" } as const;
export const flexRowSpaceAround = { ...flexRow, alignItems: "center", justifyContent: "space-around" } as const;
export const flexRowSpaceEvenly = { ...flexRow, alignItems: "center", justifyContent: "space-evenly" } as const;
export const flexRowWrap = { ...flexRow, flexWrap: "wrap" } as const;
export const flexRowNowrap = { ...flexRow, flexWrap: "nowrap" } as const;

// Common column combinations
export const flexColumnStart = { ...flexCol, alignItems: "flex-start" } as const;
export const flexColumnCenter = { ...flexCol, alignItems: "center", justifyContent: "center" } as const;
export const flexColumnBetween = { ...flexCol, justifyContent: "space-between" } as const;
export const flexColumnEnd = { ...flexCol, alignItems: "flex-end" } as const;
export const flexColumnStretch = { ...flexCol, alignItems: "stretch" } as const;

// Helpful combinations including wrapping + alignment
export const flexRowWrapCenter = { ...flexRowWrap, alignItems: "center", justifyContent: "center" } as const;
export const flexRowWrapBetween = { ...flexRowWrap, alignItems: "flex-start", justifyContent: "space-between" } as const;

// Factory helper to create flexible styles programmatically
export type FlexDirection = "row" | "column";
export type FlexAlign = "flex-start" | "flex-end" | "center" | "stretch" | "baseline";
export type FlexJustify =
	| "flex-start"
	| "flex-end"
	| "center"
	| "space-between"
	| "space-around"
	| "space-evenly";

export interface FlexOptions {
	direction?: FlexDirection;
	align?: FlexAlign;
	justify?: FlexJustify;
	wrap?: "wrap" | "nowrap" | "wrap-reverse";
	gap?: string | number;
}

export const flex = ({ direction = "row", align, justify, wrap, gap }: FlexOptions = {}) => {
	const base: Record<string, any> = { display: "flex", flexDirection: direction };
	if (align) base.alignItems = align;
	if (justify) base.justifyContent = justify;
	if (wrap) base.flexWrap = wrap;
	if (gap !== undefined) base.gap = gap;
	return base;
};

// Convenience factories
export const row = (opts: Omit<FlexOptions, "direction"> = {}) => flex({ ...opts, direction: "row" });
export const column = (opts: Omit<FlexOptions, "direction"> = {}) => flex({ ...opts, direction: "column" });

// End of flex helpers
